/**
 * AIQualityRatings — AI 생성 사진 별점 대시보드
 *
 * spec: docs/superpowers/specs/2026-05-18-ai-photo-rating-design.md §5
 *
 * 두 탭:
 *  - 통계:     GET /api/admin/ratings/stats — 프롬프트(컨셉)별 평균/분포
 *  - 평가 로그: GET /api/admin/ratings/log  — 개별 평가 목록 (RatingsLog)
 *
 * 기간 Select 는 페이지 공통 — 두 탭 모두에 적용된다.
 */

import { useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { Spinner, StatCard, Select, Tabs } from '@/ui'
import { RatingsLog } from './RatingsLog'
import styles from './AIQualityRatings.module.css'

export type Range = '7d' | '30d' | '90d' | 'all'

type SentimentKey = '1' | '2' | '3'

/** 3단계 감정 색상 (최고→별로). 분포 막대·범례·라벨 공용 단일 출처 */
const SENTIMENT: Record<SentimentKey, { label: string; emoji: string; color: string }> = {
  '3': { label: '최고에요', emoji: '😍', color: '#16a34a' },
  '2': { label: '좋아요', emoji: '😊', color: '#eab308' },
  '1': { label: '별로에요', emoji: '😕', color: '#ef4444' },
}

/** 범례/막대 표시 순서 (최고 → 별로) */
const SENTIMENT_ORDER: SentimentKey[] = ['3', '2', '1']

/** 기간 → `&from=...&to=...` 쿼리 조각 (groupBy/page 뒤에 붙임). 'all' 이면 빈 문자열. */
export function buildRangeQs(range: Range): string {
  if (range === 'all') return ''
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return `&from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`
}

const TABS = [
  { key: 'stats', label: '통계' },
  { key: 'log', label: '평가 로그' },
]

export function AIQualityRatings() {
  const [range, setRange] = useState<Range>('30d')
  const [tab, setTab] = useState<'stats' | 'log'>('stats')

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>AI 사진 별점</h1>
        <p className={styles.subtitle}>
          사용자가 평가한 점수 기반으로 프롬프트(컨셉)별 품질을 비교하고, 개별 평가를 확인합니다.
        </p>
      </div>

      <div className={styles.controlRow}>
        <Select
          label="기간"
          value={range}
          onChange={(e) => setRange(e.target.value as Range)}
          options={[
            { value: '7d', label: '최근 7일' },
            { value: '30d', label: '최근 30일' },
            { value: '90d', label: '최근 90일' },
            { value: 'all', label: '전체 기간' },
          ]}
        />
      </div>

      <Tabs items={TABS} value={tab} onChange={(k) => setTab(k as 'stats' | 'log')} />

      {tab === 'stats' ? <StatsView range={range} /> : <RatingsLog range={range} />}
    </div>
  )
}

// ── 통계 탭 ──

interface StatsRow {
  key: string
  nameKo: string | null
  imageUrl: string | null
  positiveRate: number
  sampleSize: number
  distribution: Record<SentimentKey, number>
}

interface StatsResponse {
  rows: StatsRow[]
  totalSamples: number
}

function StatsView({ range }: { range: Range }) {
  const path = useMemo(
    () => `/api/admin/ratings/stats?groupBy=concept_slug${buildRangeQs(range)}`,
    [range],
  )

  const { data, isLoading, error } = useApiFetch<StatsResponse>(path)

  const overallPositive = useMemo(() => {
    if (!data || data.rows.length === 0 || data.totalSamples === 0) return 0
    const totalWeighted = data.rows.reduce((acc, r) => acc + r.positiveRate * r.sampleSize, 0)
    return totalWeighted / data.totalSamples
  }, [data])

  return (
    <>
      <div className={styles.statRow}>
        <StatCard label="전체 표본" value={data?.totalSamples ?? 0} />
        <StatCard
          label="긍정 비율"
          value={overallPositive ? `${Math.round(overallPositive * 100)}%` : '-'}
        />
        <StatCard label="프롬프트 수" value={data?.rows.length ?? 0} />
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className={styles.errorRow}>집계 조회 실패: {String(error)}</div>
      ) : !data || data.rows.length === 0 ? (
        <div className={styles.emptyRow}>해당 기간에 평점 데이터가 없습니다.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.rankCol} aria-label="순위">#</th>
                <th className={styles.thumbCol}>사진</th>
                <th>프롬프트</th>
                <th className={styles.numCol}>긍정%</th>
                <th className={styles.numCol}>표본</th>
                <th>분포</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.key}>
                  <td className={styles.rankCell}>{i + 1}</td>
                  <td>
                    {r.imageUrl ? (
                      <img src={r.imageUrl} alt="" className={styles.thumb} loading="lazy" />
                    ) : (
                      <div className={styles.thumbPlaceholder} aria-hidden="true" />
                    )}
                  </td>
                  <td className={styles.nameCell}>
                    <span className={styles.nameKo}>{r.nameKo ?? '(이름 없음)'}</span>
                    <span className={styles.slug}>{r.key}</span>
                  </td>
                  <td className={styles.numCell}>{Math.round(r.positiveRate * 100)}%</td>
                  <td className={styles.numCell}>{r.sampleSize}</td>
                  <td className={styles.distCol}>
                    <StackedDistributionBar dist={r.distribution} sample={r.sampleSize} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.legend}>
            {SENTIMENT_ORDER.map((n) => (
              <span key={n} className={styles.legendItem}>
                <span className={styles.swatch} style={{ background: SENTIMENT[n].color }} aria-hidden="true" />
                {SENTIMENT[n].emoji} {SENTIMENT[n].label}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function StackedDistributionBar({
  dist,
  sample,
}: {
  dist: Record<SentimentKey, number>
  sample: number
}) {
  const label =
    sample > 0
      ? `평가 분포: ${SENTIMENT_ORDER.map((n) => `${SENTIMENT[n].label} ${dist[n] ?? 0}건`).join(', ')}`
      : '데이터 없음'
  return (
    <div className={styles.stack} role="img" aria-label={label}>
      {sample > 0
        ? SENTIMENT_ORDER.map((n) => {
            const v = dist[n] ?? 0
            const pct = (v / sample) * 100
            if (pct === 0) return null
            return (
              <div
                key={n}
                className={styles.stackSeg}
                style={{ width: `${pct}%`, background: SENTIMENT[n].color }}
                title={`${SENTIMENT[n].label} — ${v}건 (${pct.toFixed(1)}%)`}
              />
            )
          })
        : null}
    </div>
  )
}
